import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, arrayOf, useDataModel, extend
} from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { DrawerHeader } from '@one-thd/sui-atomic-components';
import { CreateAProjectContent } from '@thd-olt-component-react/project-forms';
import { AddToContext } from '../../AddToContext';
import { SCREENS, HEADER_TEXT } from '../../AddToConstants';
import { CopyProjectMessagingScreen } from './CopyProjectMessagingScreen';
import { AddToLoading } from '../../common/addto-loading/AddToloading';
import { SelectProject } from '../project/SelectProject';
import { useProjects, projectsModel } from '../../hooks/useProjects';

export const CopyProject = ({
  projectId,
  copyProjectData,
  initialLoad,
  setInitialLoad
}) => {
  const {
    screen,
    setScreen,
    handleClose,
    setShowSuccessMessage,
    products
  } = useContext(AddToContext);
  const [selectedProject, setSelectedProject] = useState({ projectId: '', projectName: '' });
  const { svocID } = useThdCustomer() || {};

  const pageSize = 25;
  const [page, setPage] = useState(1);
  const [loadedProjects, setLoadedProjects] = useState([]);

  const {
    LOADING,
    PROJECT_SELECT_PROJECT,
    PROJECT_CREATE_PROJECT,
    COPY_PROJECT_MESSAGING
  } = SCREENS;

  const [copyList, copyListResponse] = useDataModel('copyProjectList', {
    variables: {
      targetListName: selectedProject?.projectName,
      sourceList: copyProjectData?.sourceList,
      listItemsToInclude: copyProjectData?.items,
      targetProjectId: selectedProject?.projectId
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache',
    skip: !selectedProject?.projectId?.length
  });

  useEffect(() => {
    if (selectedProject?.projectId) {
      copyList();
    }
  }, [selectedProject?.projectId]);

  const { projects, error, refetch, loading } = useProjects({
    page,
    pageSize,
    accountId: svocID,
    filter: [],
    sort: []
  });

  const projectsCount = projects?.projects?.totalProjects || 0;

  useEffect(() => {
    refetch();
  }, [svocID, page]);

  const [hasLoadedProjects, setHasLoadedProjects] = useState(false);

  useEffect(() => {
    if (!loading) {
      setLoadedProjects((prevLoadedProjects) => {
        return [...prevLoadedProjects, ...projects?.projects?.projects || []];
      });
      setHasLoadedProjects(true);
    }
  }, [projects]);

  useEffect(() => {
    if (!initialLoad) return;

    const isLoading = loading || !hasLoadedProjects;
    const projectsExist = Boolean(loadedProjects?.length);

    if (isLoading && !projectsExist) {
      setScreen(LOADING);
    } else if (projectsExist) {
      setScreen(PROJECT_SELECT_PROJECT);
    } else if (!isLoading && !projectsExist) {
      setScreen(PROJECT_CREATE_PROJECT);
    }
  }, [loading, loadedProjects, initialLoad, hasLoadedProjects]);

  useEffect(() => {
    if (copyListResponse?.data) {
      setShowSuccessMessage(true);
      setScreen(COPY_PROJECT_MESSAGING);
    }
    if (copyListResponse?.error) {
      setScreen(COPY_PROJECT_MESSAGING);
    }
  }, [copyListResponse?.data, copyListResponse?.error]);

  const handleProjectCreation = (response) => {
    if (response?.id) {
      setSelectedProject({
        projectId: response?.id,
        projectName: response?.projectName
      });
    }
  };
  const handleBackPress = () => {
    if (screen === PROJECT_CREATE_PROJECT && !!loadedProjects?.length) {
      setScreen(PROJECT_SELECT_PROJECT);
    }
  };

  const showBackButton = !(
    (screen === PROJECT_SELECT_PROJECT && loadedProjects?.length)
    || (screen === LOADING)
    || (screen === PROJECT_CREATE_PROJECT && !loadedProjects?.length)
    || (screen === COPY_PROJECT_MESSAGING)
  );

  return (
    <>
      <DrawerHeader
        data-testid="add-to-drawer-header"
        title={HEADER_TEXT[screen] ? HEADER_TEXT[screen] : 'Add Item to Project'}
        onClose={handleClose}
        onBack={showBackButton ? handleBackPress : null}
      />
      {screen === LOADING && (
        <AddToLoading />
      )}
      {screen === PROJECT_CREATE_PROJECT && (
        <CreateAProjectContent onCreate={handleProjectCreation} handleClose={handleClose} isAddToDrawer />
      )}
      {screen === PROJECT_SELECT_PROJECT && (
        <SelectProject
          products={products}
          singleProduct={[]}
          multipleProduct={[]}
          projects={loadedProjects}
          projectsExist={!!loadedProjects?.length}
          setSelectedProject={setSelectedProject}
          pageSize={pageSize}
          projectsCount={projectsCount}
          loading={loading}
          setInitialLoad={setInitialLoad}
          setPage={setPage}
          page={page}
          showInlineSuccessAlert={false}
          onInlineCreationSuccess={({ projectId: newProjectId, projectName }) => {
            setSelectedProject({ projectId: newProjectId, projectName });
          }}
        />
      )}
      {screen === COPY_PROJECT_MESSAGING && (
        <CopyProjectMessagingScreen
          selectedProject={selectedProject}
          copyProjectItems={copyProjectData?.items}
        />
      )}
    </>
  );
};

CopyProject.displayName = 'CopyProject';

CopyProject.propTypes = {
  projectId: PropTypes.string,
  copyProjectData: PropTypes.shape({
    targetListName: PropTypes.string,
    sourceList: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.string),
    targetProjectId: PropTypes.string
  }).isRequired,
  initialLoad: PropTypes.bool.isRequired,
  setInitialLoad: PropTypes.func.isRequired
};

CopyProject.defaultProps = {
  projectId: null
};

CopyProject.dataModel = extend({
  copyProjectList: params({
    targetListName: string(),
    sourceList: string(),
    listItemsToInclude: arrayOf(string()),
    targetProjectId: string().isRequired()
  }).mutation().shape({
    projectListId: string()
  })
},
projectsModel
);
